import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildApprovalRows,
  clone,
  computeUserRole,
  getDepartmentByUserMap,
  getInitials,
} from "./departmentsData";
import {
  createDepartment,
  createUnit,
  deleteDepartment as deleteDepartmentApi,
  deleteUnit as deleteUnitApi,
  getDepartments,
  getOrganisationUsers,
  syncDepartmentMembers,
  toDepartmentView,
  updateDepartment,
  updateUnit,
} from "./departmentsApi";
import "./departments.css";

const EMPTY_DEPT_FORM = { name: "", description: "", hasUnits: "yes", head: "" };
const EMPTY_UNIT_FORM = { name: "", description: "", head: "" };

const DepartmentsPage = () => {
  const [organisationUsers, setOrganisationUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  // Modals
  const [deptModal, setDeptModal] = useState(null); // { index|null, form }
  const [unitModal, setUnitModal] = useState(null); // { departmentIndex, unitIndex|null, form }
  const [membersModal, setMembersModal] = useState(null); // { departmentIndex, draft, search }
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, departmentIndex, unitIndex? }

  const notify = useCallback((message) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 3000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [departmentResponse, userResponse] = await Promise.all([getDepartments(), getOrganisationUsers()]);
      const nextDepartments = (departmentResponse.data || []).map(toDepartmentView);
      setDepartments(nextDepartments);
      setOrganisationUsers((userResponse.data?.data || []).map((user) => ({ ...user, status: user.status ? "Active" : "Inactive" })));
      setExpanded(new Set(nextDepartments.map((department, index) => department.hasUnits ? index : -1).filter((index) => index >= 0)));
      setSelectedIndex((current) => Math.min(current, Math.max(0, nextDepartments.length - 1)));
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { loadData(); return () => clearTimeout(toastTimer.current); }, [loadData]);

  const commit = (next) => {
    setDepartments(next);
  };

  const selected = departments[selectedIndex] || null;
  const approvalRows = useMemo(() => buildApprovalRows(selected, departments), [selected, departments]);
  const departmentByUser = useMemo(() => getDepartmentByUserMap(departments), [departments]);

  /* ---------------- Departments ---------------- */
  const openAddDepartment = () => setDeptModal({ index: null, form: { ...EMPTY_DEPT_FORM } });
  const openEditDepartment = (index) => {
    const d = departments[index];
    setDeptModal({ index, form: { name: d.name, description: d.description, hasUnits: d.hasUnits ? "yes" : "no", head: d.headId || "" } });
  };

  const saveDepartment = async () => {
    const { index, form } = deptModal;
    if (!form.name.trim() || !form.description.trim()) return notify("Please complete department name and description.");
    const payload = { name: form.name.trim(), description: form.description.trim(), has_units: form.hasUnits === "yes", hod_user_id: form.head ? Number(form.head) : null, status: true };
    setSaving(true);
    try {
      if (index !== null) await updateDepartment(departments[index].id, { ...payload, approval_skips: departments[index].approvalSkips || {} });
      else await createDepartment(payload);
      setDeptModal(null);
      notify(index !== null ? "Department updated successfully" : "Department added successfully");
      await loadData();
    } catch (error) { notify(error.message); } finally { setSaving(false); }
  };

  const deleteDepartment = async (index) => {
    setSaving(true);
    try {
      await deleteDepartmentApi(departments[index].id);
      setSelectedIndex(0); setConfirmDelete(null); notify("Department removed successfully"); await loadData();
    } catch (error) { notify(error.message); } finally { setSaving(false); }
  };

  /* ---------------- Units ---------------- */
  const openAddUnit = (departmentIndex) => {
    setSelectedIndex(departmentIndex);
    setExpanded((prev) => new Set(prev).add(departmentIndex));
    setUnitModal({ departmentIndex, unitIndex: null, form: { ...EMPTY_UNIT_FORM } });
  };
  const openEditUnit = (departmentIndex, unitIndex) => {
    const unit = departments[departmentIndex].units[unitIndex];
    setUnitModal({ departmentIndex, unitIndex, form: { name: unit.name, description: unit.description, head: unit.headId || "" } });
  };

  const saveUnit = async () => {
    const { departmentIndex, unitIndex, form } = unitModal;
    if (!form.name.trim() || !form.description.trim()) return notify("Please complete unit name and description.");
    const department = departments[departmentIndex];
    const payload = { department_id: department.id, name: form.name.trim(), description: form.description.trim(), head_user_id: form.head ? Number(form.head) : null, status: true };
    setSaving(true);
    try {
      if (unitIndex !== null) await updateUnit(department.units[unitIndex].id, payload); else await createUnit(payload);
      setUnitModal(null); notify(unitIndex !== null ? "Unit updated successfully" : "Unit added successfully"); await loadData();
    } catch (error) { notify(error.message); } finally { setSaving(false); }
  };

  const deleteUnit = async (departmentIndex, unitIndex) => {
    setSaving(true);
    try {
      await deleteUnitApi(departments[departmentIndex].units[unitIndex].id);
      setConfirmDelete(null); notify("Unit removed successfully"); await loadData();
    } catch (error) { notify(error.message); } finally { setSaving(false); }
  };

  /* ---------------- Manage Team ---------------- */
  const openManageMembers = (departmentIndex) => {
    setSelectedIndex(departmentIndex);
    setMembersModal({ departmentIndex, draft: clone(departments[departmentIndex].members || []), search: "" });
  };

  const toggleMember = (user, checked) => {
    setMembersModal((modal) => {
      const draft = checked
        ? [...modal.draft, { id: user.id, name: user.name, email: user.email, units: [] }]
        : modal.draft.filter((m) => m.name !== user.name);
      return { ...modal, draft };
    });
  };

  const toggleMemberUnit = (userName, unitName, checked) => {
    setMembersModal((modal) => ({
      ...modal,
      draft: modal.draft.map((m) =>
        m.name === userName
          ? { ...m, units: checked ? Array.from(new Set([...(m.units || []), unitName])) : (m.units || []).filter((n) => n !== unitName) }
          : m
      ),
    }));
  };

  const saveMembers = async () => {
    const department = departments[membersModal.departmentIndex];
    const members = membersModal.draft.map((member) => ({
      user_id: member.id,
      unit_ids: (member.units || []).map((name) => department.units.find((unit) => unit.name === name)?.id).filter(Boolean),
    }));
    setSaving(true);
    try {
      await syncDepartmentMembers(department.id, members);
      setMembersModal(null); notify("Member assignments updated"); await loadData();
    } catch (error) { notify(error.message); } finally { setSaving(false); }
  };

  /* ---------------- Approval skips ---------------- */
  const toggleSkip = async (key, checked) => {
    const next = clone(departments);
    if (!next[selectedIndex].approvalSkips) next[selectedIndex].approvalSkips = {};
    next[selectedIndex].approvalSkips[key] = checked;
    commit(next);
    try {
      const department = next[selectedIndex];
      await updateDepartment(department.id, { name: department.name, description: department.description, hod_user_id: department.headId, has_units: department.hasUnits, status: department.status === "Active", approval_skips: department.approvalSkips });
      notify(checked ? "Approval layer skipped for this department" : "Approval layer restored");
    } catch (error) { notify(error.message); await loadData(); }
  };

  /* ---------------- Render helpers ---------------- */
  const renderApproverCell = (cell) => {
    if (cell.type === "person") {
      return (
        <span>
          {cell.label} {cell.tag ? <small className="bes-muted">({cell.tag})</small> : null}
        </span>
      );
    }
    if (cell.type === "none") return <span>—</span>;
    return <span className="bes-muted bes-italic">{cell.label}</span>;
  };

  const skips = (selected && selected.approvalSkips) || {};

  return (
    <div className="bes-departments">
      <div className="bes-page-head">
        <div>
          <h2>Departments</h2>
          <p>Manage department master data used across user profiles and approvals.</p>
        </div>
        <button className="bes-btn bes-btn-primary" onClick={openAddDepartment}>
          <i className="ri-add-line" /> Add Department
        </button>
      </div>

      <div className="bes-card">
        <div className="bes-info-alert">
          <i className="ri-information-line" />
          <div>
            <strong>Department and unit governance</strong>
            <p>
              Each department can optionally use units. Roles are calculated from the structure: a department head
              becomes <strong>Head of Department</strong> and a unit head becomes <strong>Head of Unit</strong>, chosen
              from the department's assigned members. Each user can belong to <strong>only one department</strong>, and
              can be assigned to <strong>multiple units</strong> within it.
            </p>
          </div>
        </div>

        <div className="bes-toolbar">
          <span>Organisation structure</span>
          <button
            className="bes-btn bes-btn-outline"
            onClick={() =>
              setExpanded((prev) =>
                prev.size ? new Set() : new Set(departments.map((d, i) => (d.hasUnits ? i : -1)).filter((i) => i >= 0))
              )
            }
          >
            {expanded.size ? "Collapse All" : "Expand All"}
          </button>
        </div>

        <div className="bes-table-wrap">
          <table className="bes-table">
            <thead>
              <tr>
                <th style={{ width: "20%" }}>Department / Unit</th>
                <th style={{ width: "24%" }}>Description</th>
                <th style={{ width: "22%" }}>Leadership &amp; Members</th>
                <th style={{ width: "24%" }}>Members</th>
                <th style={{ width: "10%", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && !departments.length && <tr><td colSpan="5" className="bes-center bes-muted">Loading departments...</td></tr>}
              {departments.map((department, index) => {
                const members = department.members || [];
                const isExpanded = expanded.has(index);
                return (
                  <React.Fragment key={department.name + index}>
                    <tr
                      className={`bes-dept-row ${index === selectedIndex ? "is-selected" : ""}`}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <td>
                        <button
                          className="bes-toggle"
                          disabled={!department.hasUnits}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIndex(index);
                            setExpanded((prev) => {
                              const next = new Set(prev);
                              if (next.has(index)) next.delete(index);
                              else next.add(index);
                              return next;
                            });
                          }}
                        >
                          <i className={`ri-arrow-${department.hasUnits ? (isExpanded ? "down" : "right") : "right"}-s-line`} />
                          <strong>{department.name}</strong>
                        </button>
                      </td>
                      <td className="bes-muted">{department.description}</td>
                      <td>
                        <div className="bes-head-display">
                          <i className={`ri-user-star-line ${department.head ? "" : "bes-muted"}`} />
                          {department.head ? <strong>{department.head}</strong> : <span className="bes-muted bes-italic">No head assigned</span>}
                        </div>
                      </td>
                      <td>
                        <button
                          className="bes-link-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openManageMembers(index);
                          }}
                        >
                          <i className="ri-team-line" /> Manage Team · {members.length}
                        </button>
                        {members.length ? (
                          <div className="bes-chip-list">
                            {members.map((member) => (
                              <span key={member.name} className="bes-chip" title={computeUserRole(member.name, departments)}>
                                {member.name}
                                {(member.units || []).length ? <small>{member.units.join(", ")}</small> : null}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="bes-muted bes-italic bes-block">No members assigned</span>
                        )}
                      </td>
                      <td className="bes-center">
                        <div className="bes-actions">
                          <button className="bes-icon-btn" title="Add Unit" onClick={(e) => { e.stopPropagation(); openAddUnit(index); }}>
                            <i className="ri-add-line" />
                          </button>
                          <button className="bes-icon-btn" title="Edit Department" onClick={(e) => { e.stopPropagation(); openEditDepartment(index); }}>
                            <i className="ri-pencil-line" />
                          </button>
                          <button className="bes-icon-btn" title="Delete Department" onClick={(e) => { e.stopPropagation(); setConfirmDelete({ type: "department", departmentIndex: index }); }}>
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {department.hasUnits && isExpanded &&
                      (department.units.length ? (
                        department.units.map((unit, unitIndex) => {
                          const unitMembers = members.filter((m) => (m.units || []).includes(unit.name));
                          return (
                            <tr key={unit.name + unitIndex} className="bes-unit-row">
                              <td>
                                <div className="bes-unit-name">
                                  <span className="bes-unit-pill">Unit</span>
                                  <strong>{unit.name}</strong>
                                </div>
                              </td>
                              <td className="bes-muted">{unit.description}</td>
                              <td>
                                <div className="bes-head-display">
                                  <i className={`ri-user-star-line ${unit.head ? "" : "bes-muted"}`} />
                                  {unit.head ? <strong>{unit.head}</strong> : <span className="bes-muted bes-italic">No head assigned</span>}
                                </div>
                              </td>
                              <td>
                                {unitMembers.length ? (
                                  <div className="bes-chip-list">
                                    {unitMembers.map((m) => (
                                      <span key={m.name} className="bes-chip">{m.name}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="bes-muted bes-italic">No members</span>
                                )}
                              </td>
                              <td className="bes-center">
                                <div className="bes-actions">
                                  <button className="bes-icon-btn" title="Edit Unit" onClick={() => openEditUnit(index, unitIndex)}>
                                    <i className="ri-pencil-line" />
                                  </button>
                                  <button className="bes-icon-btn" title="Delete Unit" onClick={() => setConfirmDelete({ type: "unit", departmentIndex: index, unitIndex })}>
                                    <i className="ri-delete-bin-line" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr className="bes-unit-row">
                          <td colSpan={4}>
                            <span className="bes-muted"><i className="ri-node-tree" /> No units added under this department yet.</span>
                          </td>
                          <td className="bes-center">
                            <button className="bes-btn bes-btn-outline" onClick={() => openAddUnit(index)}>
                              <i className="ri-add-line" /> Add Unit
                            </button>
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approval flow — derived from the structure */}
      <div className="bes-card">
        <div className="bes-toolbar">
          <span>
            Approval flow — <span className="bes-accent">{selected ? selected.name : ""}</span>
          </span>
          <span className="bes-toolbar-hint">Derived from the organisation structure · click a department row above to preview its routing</span>
        </div>

        <div className="bes-skip-row">
          <span className="bes-skip-label"><i className="ri-skip-forward-line" /> Skip approval layer for this department:</span>
          <label><input type="checkbox" checked={!!skips.unitHead} onChange={(e) => toggleSkip("unitHead", e.target.checked)} /> Head of Unit</label>
          <label><input type="checkbox" checked={!!skips.departmentHead} onChange={(e) => toggleSkip("departmentHead", e.target.checked)} /> Head of Department</label>
          <label><input type="checkbox" checked={!!skips.ceo} onChange={(e) => toggleSkip("ceo", e.target.checked)} /> CEO</label>
        </div>

        <div className="bes-table-wrap">
          <table className="bes-table">
            <thead>
              <tr>
                <th style={{ width: "34%" }}>Applicant</th>
                <th style={{ width: "33%" }}>1st Approval</th>
                <th style={{ width: "33%" }}>2nd Approval</th>
              </tr>
            </thead>
            <tbody>
              {approvalRows.map((row, index) => (
                <tr key={index}>
                  <td><strong>{row.applicant}</strong></td>
                  <td>{renderApproverCell(row.first)}</td>
                  <td>{renderApproverCell(row.second)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------------- Department modal ---------------- */}
      {deptModal && (
        <div className="bes-modal-overlay" onClick={() => setDeptModal(null)}>
          <div className="bes-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{deptModal.index !== null ? "Edit Department" : "Add Department"}</h3>
            <label className="bes-field">
              <span>Department Name *</span>
              <input value={deptModal.form.name} onChange={(e) => setDeptModal({ ...deptModal, form: { ...deptModal.form, name: e.target.value } })} placeholder="e.g. Finance" />
            </label>
            <label className="bes-field">
              <span>Description *</span>
              <textarea rows={3} value={deptModal.form.description} onChange={(e) => setDeptModal({ ...deptModal, form: { ...deptModal.form, description: e.target.value } })} placeholder="Enter department scope..." />
            </label>
            <label className="bes-field">
              <span>Uses Unit Structure?</span>
              <select value={deptModal.form.hasUnits} onChange={(e) => setDeptModal({ ...deptModal, form: { ...deptModal.form, hasUnits: e.target.value } })}>
                <option value="yes">Yes, this department has units</option>
                <option value="no">No, department operates without units</option>
              </select>
            </label>
            <label className="bes-field">
              <span>Head of Department</span>
              <select value={deptModal.form.head} onChange={(e) => setDeptModal({ ...deptModal, form: { ...deptModal.form, head: e.target.value } })}>
                <option value="">Leave unassigned</option>
                {(() => {
                  const members = deptModal.index !== null ? (departments[deptModal.index].members || []) : [];
                  return members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>);
                })()}
              </select>
              <small>Only members assigned to this department are listed. Assigning a head grants them the Head of Department role.</small>
            </label>
            <div className="bes-modal-footer">
              <button className="bes-btn bes-btn-outline" onClick={() => setDeptModal(null)}>Cancel</button>
              <button className="bes-btn bes-btn-primary" disabled={saving} onClick={saveDepartment}>{saving ? "Saving..." : "Save Department"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Unit modal ---------------- */}
      {unitModal && (
        <div className="bes-modal-overlay" onClick={() => setUnitModal(null)}>
          <div className="bes-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{unitModal.unitIndex !== null ? `Edit Unit in ${departments[unitModal.departmentIndex].name}` : `Add Unit to ${departments[unitModal.departmentIndex].name}`}</h3>
            <label className="bes-field">
              <span>Unit Name *</span>
              <input value={unitModal.form.name} onChange={(e) => setUnitModal({ ...unitModal, form: { ...unitModal.form, name: e.target.value } })} placeholder="e.g. Payroll" />
            </label>
            <label className="bes-field">
              <span>Description *</span>
              <textarea rows={3} value={unitModal.form.description} onChange={(e) => setUnitModal({ ...unitModal, form: { ...unitModal.form, description: e.target.value } })} placeholder="Enter unit scope..." />
            </label>
            <label className="bes-field">
              <span>Head of Unit</span>
              <select value={unitModal.form.head} onChange={(e) => setUnitModal({ ...unitModal, form: { ...unitModal.form, head: e.target.value } })}>
                <option value="">Leave unassigned</option>
                {(() => {
                  const members = departments[unitModal.departmentIndex].members || [];
                  return members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>);
                })()}
              </select>
              <small>Only members assigned to this department are listed. Assigning a head grants them the Head of Unit role.</small>
            </label>
            <div className="bes-modal-footer">
              <button className="bes-btn bes-btn-outline" onClick={() => setUnitModal(null)}>Cancel</button>
              <button className="bes-btn bes-btn-primary" disabled={saving} onClick={saveUnit}>{saving ? "Saving..." : "Save Unit"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Manage Team modal ---------------- */}
      {membersModal && (
        <div className="bes-modal-overlay" onClick={() => setMembersModal(null)}>
          <div className="bes-modal bes-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>{departments[membersModal.departmentIndex].name}</h3>
            <p className="bes-muted">
              Each user can belong to only one department. Tick the units they serve in — multiple units are allowed.
              Roles come from the org structure.
            </p>
            <div className="bes-field">
              <input
                type="search"
                placeholder="Search user by name or email..."
                value={membersModal.search}
                onChange={(e) => setMembersModal({ ...membersModal, search: e.target.value })}
              />
            </div>
            <div className="bes-member-heading">
              <span>Available users</span>
              <span>{membersModal.draft.length} selected</span>
            </div>
            <div className="bes-member-list">
              {organisationUsers.filter((user) => {
                const q = membersModal.search.trim().toLowerCase();
                return !q || `${user.name} ${user.email}`.toLowerCase().includes(q);
              }).map((user) => {
                const department = departments[membersModal.departmentIndex];
                const assignment = membersModal.draft.find((m) => m.name === user.name);
                const otherDepartment = departmentByUser.get(user.name);
                const isLocked = !assignment && otherDepartment && otherDepartment !== department.name;
                const role = computeUserRole(user.name, departments);
                return (
                  <div key={user.name} className={`bes-member-row ${isLocked ? "is-locked" : ""}`}>
                    <label className="bes-member-identity">
                      <input
                        type="checkbox"
                        checked={!!assignment}
                        disabled={isLocked}
                        onChange={(e) => toggleMember(user, e.target.checked)}
                      />
                      <span className="bes-avatar">{getInitials(user.name)}</span>
                      <span>
                        <strong>{user.name}</strong>
                        <small>{user.email}</small>
                        {isLocked && (
                          <small className="bes-locked-note">
                            <i className="ri-lock-line" /> Assigned to {otherDepartment}
                          </small>
                        )}
                      </span>
                    </label>
                    <div className="bes-unit-tags">
                      {(department.units || []).length ? (
                        department.units.map((unit) => (
                          <label key={unit.name} className={`bes-unit-tag ${assignment ? "" : "is-muted"}`}>
                            <input
                              type="checkbox"
                              disabled={!assignment}
                              checked={!!assignment && (assignment.units || []).includes(unit.name)}
                              onChange={(e) => toggleMemberUnit(user.name, unit.name, e.target.checked)}
                            />
                            {unit.name}
                          </label>
                        ))
                      ) : (
                        <span className="bes-muted bes-italic">No units in this department</span>
                      )}
                    </div>
                    <span className="bes-role-label">{role}</span>
                  </div>
                );
              })}
            </div>
            <div className="bes-modal-footer">
              <button className="bes-btn bes-btn-outline" onClick={() => setMembersModal(null)}>Cancel</button>
              <button className="bes-btn bes-btn-primary" disabled={saving} onClick={saveMembers}>{saving ? "Saving..." : "Save Members"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Delete confirmations ---------------- */}
      {confirmDelete && (
        <div className="bes-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="bes-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              <i className="ri-error-warning-line bes-danger" />{" "}
              {confirmDelete.type === "department" ? "Delete Department" : "Delete Unit"}
            </h3>
            <p>
              Are you sure you want to delete{" "}
              <strong>
                {confirmDelete.type === "department"
                  ? departments[confirmDelete.departmentIndex].name
                  : departments[confirmDelete.departmentIndex].units[confirmDelete.unitIndex].name}
              </strong>
              ?
            </p>
            <div className="bes-modal-footer">
              <button className="bes-btn bes-btn-outline" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                className="bes-btn bes-btn-danger"
                onClick={() =>
                  confirmDelete.type === "department"
                    ? deleteDepartment(confirmDelete.departmentIndex)
                    : deleteUnit(confirmDelete.departmentIndex, confirmDelete.unitIndex)
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="bes-toast">{toast}</div>}
    </div>
  );
};

export default DepartmentsPage;
